import React, { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import Modal from "@/components/ui/modal";
import { Close, Check } from "@/components/icons";
import PollTextarea from "@/components/ui/poll-textarea";
import PollSelect from "@/components/ui/poll-select";
import PollRadio from "@/components/ui/poll-radio";
import PollCheckbox, { PollCheckboxQuestion } from "@/components/ui/poll-checkbox";
import PollStar from "@/components/ui/poll-star";
import PollNumbers from "@/components/ui/poll-numbers";
import { clientSurveysApi } from "@/features/client-surveys";
import MatrixQuestionTable from "@/features/polls/components/MatrixQuestionTable";
import type {
  ClientSurveyQuestionResDto,
  ClientSurveyQuestionOptionDto,
  ClientSurveyQuestionsDataDto,
  ClientSurveyQuestionType,
} from "@/features/client-surveys";
import {
  resolveFollowUpAction,
  type EducationFollowUpAction,
} from "@/features/client-educations/utils/follow-up-url";
import styles from "./PollAnswerModal.module.scss";

const QUESTION_TYPE_METIN = 1;
const QUESTION_TYPE_DROPDOWN = 2;
const QUESTION_TYPE_SINGLE = 3;
const QUESTION_TYPE_MULTIPLE = 4;
const QUESTION_TYPE_STAR = 5;
const QUESTION_TYPE_SCALE = 6;

function getOptionLabel(opt: ClientSurveyQuestionOptionDto): string {
  return opt.optionText ?? opt.label ?? opt.text ?? opt.id;
}

export type PollAnswerValue = string | string[] | number;

export interface PollAnswerModalProps {
  educationId: string | null;
  pollTitle: string;
  isOpen: boolean;
  readOnly?: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export const PollAnswerModal: React.FC<PollAnswerModalProps> = ({
  educationId,
  pollTitle,
  isOpen,
  readOnly = false,
  onClose,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<ClientSurveyQuestionsDataDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [answers, setAnswers] = useState<Record<string, PollAnswerValue>>({});
  const [isCompleted, setIsCompleted] = useState(false);
  // BE submit sonrası bir sonraki aşama popup'ı ister dönerse onu saklarız
  const [pendingFollowUp, setPendingFollowUp] =
    useState<EducationFollowUpAction | null>(null);

  const fetchQuestions = useCallback(() => {
    if (!educationId || !isOpen) return;
    setLoading(true);
    setError(null);
    setAnswers({});
    clientSurveysApi
      .getSurveyQuestions(educationId)
      .then((res) => {
        // Backend artık anket tanımlı değilse 404 yerine boş response dönüyor.
        if (!res || !res.questions || res.questions.length === 0) {
          setData(null);
          setError(
            t("clientPolls.modal.noSurvey"),
          );
          return;
        }
        setData(res);
        const initial: Record<string, PollAnswerValue> = {};
        res.questions.forEach((q) => {
          const firstSelectedId = q.selectedOptionIds?.[0];
          if (q.type === QUESTION_TYPE_MULTIPLE && q.selectedOptionIds?.length) {
            initial[q.id] = [...q.selectedOptionIds];
          } else if (
            (q.type === QUESTION_TYPE_SINGLE || q.type === QUESTION_TYPE_DROPDOWN) &&
            (firstSelectedId ?? q.selectedOptionTextValue)
          ) {
            initial[q.id] = (firstSelectedId ?? q.selectedOptionTextValue) ?? "";
          } else if (q.type === QUESTION_TYPE_METIN && q.textAnswer) {
            initial[q.id] = q.textAnswer;
          } else if (
            (q.type === QUESTION_TYPE_STAR || q.type === QUESTION_TYPE_SCALE) &&
            firstSelectedId
          ) {
            // Backend option ID döndürüyor; PollStar/PollNumbers numericValue
            // bekliyor — option listesinden eşleştirip numerik değeri kullan.
            const opt = (q.options ?? []).find((o) => o.id === firstSelectedId);
            const numeric =
              opt?.numericValue ??
              opt?.order ??
              Number(firstSelectedId);
            initial[q.id] = Number.isFinite(numeric) ? Number(numeric) : 0;
          }
        });
        setAnswers(initial);
      })
      .catch(() => {
        setError(t("clientPolls.modal.errorLoad"));
        setData(null);
      })
      .finally(() => setLoading(false));
  }, [educationId, isOpen, t]);

  useEffect(() => {
    if (isOpen && educationId) fetchQuestions();
    if (!isOpen) {
      setData(null);
      setError(null);
      setAnswers({});
      setIsCompleted(false);
      setPendingFollowUp(null);
    }
  }, [isOpen, educationId, fetchQuestions]);

  const setAnswer = useCallback((questionId: string, value: PollAnswerValue) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  }, []);

  /**
   * Bir cevap değerinin o soru tipi için "dolu" sayılıp sayılmayacağını belirler.
   * - Metin: trim edilmiş string uzunluğu > 0
   * - Dropdown/Single: seçim boş string olmayan string
   * - Multiple: en az 1 seçili opsiyon
   * - Star/Scale: 1 veya daha büyük numerik değer (0 = henüz seçilmedi)
   */
  const isAnswerFilled = useCallback(
    (q: ClientSurveyQuestionResDto, value: PollAnswerValue | undefined): boolean => {
      switch (q.type as ClientSurveyQuestionType) {
        case QUESTION_TYPE_METIN:
          return typeof value === "string" && value.trim().length > 0;
        case QUESTION_TYPE_DROPDOWN:
        case QUESTION_TYPE_SINGLE:
          return typeof value === "string" && value.length > 0;
        case QUESTION_TYPE_MULTIPLE:
          return Array.isArray(value) && value.length > 0;
        case QUESTION_TYPE_STAR:
        case QUESTION_TYPE_SCALE:
          return typeof value === "number" && value > 0;
        default:
          return value !== undefined && value !== null;
      }
    },
    [],
  );

  const handleFinish = useCallback(async () => {
    if (!educationId || !data?.questions?.length) {
      onClose();
      return;
    }

    // Zorunlu alanlar dolmadan bitir tıklanırsa net liste ile uyar.
    // (isRequired ?? isRequried — BE alan adı geçici tutarsız.)
    const missingIndices: number[] = [];
    data.questions.forEach((q, idx) => {
      const required = q.isRequired ?? q.isRequried ?? false;
      if (!required) return;
      if (!isAnswerFilled(q, answers[q.id])) {
        missingIndices.push(idx + 1);
      }
    });
    if (missingIndices.length > 0) {
      const list = missingIndices.join(", ");
      setError(
        t(
          "clientPolls.modal.requiredMissing",
          t("clientPolls.requiredMissing"),
          { count: missingIndices.length, list },
        ),
      );
      // İlk eksik soruya scroll — DOM'da data-question-index üzerinden.
      const first = missingIndices[0];
      const el = document.querySelector<HTMLElement>(
        `[data-question-index="${first}"]`,
      );
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
      return;
    }

    setError(null);

    const payload = {
      answers: data.questions.map((q) => {
        const base = { questionId: q.id };
        const value = answers[q.id];

        switch (q.type as ClientSurveyQuestionType) {
          case QUESTION_TYPE_METIN:
            return {
              ...base,
              textValue: typeof value === "string" ? value : q.textAnswer ?? undefined,
            };

          case QUESTION_TYPE_DROPDOWN:
          case QUESTION_TYPE_SINGLE: {
            const selected = (value as string) ?? q.selectedOptionIds?.[0];
            return {
              ...base,
              selectedOptionId: selected,
            };
          }

          case QUESTION_TYPE_MULTIPLE: {
            const selectedIds =
              (Array.isArray(value) ? value : q.selectedOptionIds) ?? [];
            return {
              ...base,
              selectedOptionIds: selectedIds.length ? selectedIds : undefined,
            };
          }

          case QUESTION_TYPE_STAR:
          case QUESTION_TYPE_SCALE: {
            const numeric =
              typeof value === "number"
                ? value
                : Number((q.selectedOptionIds ?? [])[0] ?? 0);
            const safeNumeric = Number.isNaN(numeric) ? 0 : numeric;
            const opts = q.options ?? [];
            const matchedOpt =
              opts.find((o) => o.numericValue === safeNumeric) ??
              opts.find((o) => o.order === safeNumeric);
            return {
              ...base,
              selectedOptionId: matchedOpt?.id,
            };
          }

          default:
            return base;
        }
      }),
    };

    setSubmitLoading(true);
    try {
      const result = await clientSurveysApi.putSurveyAnswersBulk(
        educationId,
        payload,
      );
      if (result.followUpAction) {
        // BE bir sonraki adım için popup istiyor (örn. anket hâlâ tamamlanmadıysa)
        setPendingFollowUp(result.followUpAction);
      } else {
        // followUpAction null → anket kapandı (ya tamamlandı ya da hâlâ ENROLLED'da)
        // Mevcut başarı ekranını göster; kullanıcı "Sertifikaya Git"i kendisi tıklayabilir.
        setIsCompleted(true);
        onSuccess?.();
      }
    } catch (err) {
      // Backend anlamlı bir mesaj dönüyorsa onu göster (örn. "Bu soruya cevap
      // zorunludur"); aksi halde jenerik fallback.
      const backendMessage =
        (err as { response?: { data?: { message?: string | string[] } } })?.response
          ?.data?.message;
      let userMessage: string | null = null;
      if (Array.isArray(backendMessage)) {
        userMessage = backendMessage.filter(Boolean).join(" • ");
      } else if (typeof backendMessage === "string" && backendMessage.trim()) {
        userMessage = backendMessage;
      }
      setError(
        userMessage ??
          t("clientPolls.modal.errorSubmit"),
      );
    } finally {
      setSubmitLoading(false);
    }
  }, [answers, data, educationId, isAnswerFilled, onSuccess, t]);

  const renderQuestion = (q: ClientSurveyQuestionResDto, index: number) => {
    const required = q.isRequired ?? q.isRequried ?? false;
    const labelContent = (
      <>
        {index + 1}) {q.questionText}
        {required && <span className={styles.questionRequired}>*</span>}
      </>
    );
    const label = (
      <span className={styles.questionLabel}>
        {labelContent}
      </span>
    );

    switch (q.type as ClientSurveyQuestionType) {
      case QUESTION_TYPE_METIN: {
        const value = (answers[q.id] as string) ?? q.textAnswer ?? "";
        return (
          <div
            key={q.id}
            className={styles.questionBlock}
            data-question-index={index + 1}
          >
            {label}
            <PollTextarea
              value={value}
              onChange={(e) => setAnswer(q.id, e.target.value)}
              placeholder={t("clientPolls.modal.textPlaceholder")}
            />
          </div>
        );
      }

      case QUESTION_TYPE_DROPDOWN: {
        const options = (q.options ?? []).map((o) => ({
          label: getOptionLabel(o),
          value: o.id,
        }));
        const value = (answers[q.id] as string) ?? q.selectedOptionIds?.[0] ?? "";
        return (
          <div
            key={q.id}
            className={styles.questionBlock}
            data-question-index={index + 1}
          >
            {label}
            <PollSelect
              options={options}
              value={value || null}
              onChange={(v) => setAnswer(q.id, v)}
              placeholder={t("clientPolls.modal.selectPlaceholder")}
            />
          </div>
        );
      }

      case QUESTION_TYPE_SINGLE: {
        const opts = q.options ?? [];
        const selected = (answers[q.id] as string) ?? q.selectedOptionIds?.[0] ?? "";
        return (
          <div
            key={q.id}
            className={styles.questionBlock}
            data-question-index={index + 1}
          >
            {label}
            <div className={styles.optionsGroup}>
              {opts.map((opt) => (
                <PollRadio
                  key={opt.id}
                  name={`q-${q.id}`}
                  label={getOptionLabel(opt)}
                  value={opt.id}
                  checked={selected === opt.id}
                  onChange={(v) => setAnswer(q.id, v)}
                />
              ))}
            </div>
          </div>
        );
      }

      case QUESTION_TYPE_MULTIPLE: {
        const opts = q.options ?? [];
        const selectedIds = (answers[q.id] as string[]) ?? q.selectedOptionIds ?? [];
        const arr = Array.isArray(selectedIds) ? selectedIds : [];
        const toggle = (optId: string, checked: boolean) => {
          if (checked) setAnswer(q.id, [...arr.filter((id) => id !== optId), optId]);
          else setAnswer(q.id, arr.filter((id) => id !== optId));
        };
        return (
          <div
            key={q.id}
            className={styles.questionBlock}
            data-question-index={index + 1}
          >
            <PollCheckboxQuestion>{labelContent}</PollCheckboxQuestion>
            <div className={styles.optionsGroup}>
              {opts.map((opt) => (
                <PollCheckbox
                  key={opt.id}
                  label={getOptionLabel(opt)}
                  value={opt.id}
                  checked={arr.includes(opt.id)}
                  onChange={(checked) => toggle(opt.id, checked)}
                />
              ))}
            </div>
          </div>
        );
      }

      case QUESTION_TYPE_STAR: {
        const value = Number(answers[q.id]) || 0;
        return (
          <div
            key={q.id}
            className={styles.questionBlock}
            data-question-index={index + 1}
          >
            {label}
            <PollStar
              value={value}
              onChange={(v) => setAnswer(q.id, v)}
              maxStars={5}
            />
          </div>
        );
      }

      case QUESTION_TYPE_SCALE: {
        const value = Number(answers[q.id]) || 0;
        return (
          <div
            key={q.id}
            className={styles.questionBlock}
            data-question-index={index + 1}
          >
            {label}
            <PollNumbers
              value={value}
              onChange={(v) => setAnswer(q.id, v)}
              min={1}
              max={5}
              labelMin={t("clientPolls.modal.worst")}
              labelMax={t("clientPolls.modal.best")}
            />
          </div>
        );
      }

      default:
        return (
          <div
            key={q.id}
            className={styles.questionBlock}
            data-question-index={index + 1}
          >
            {label}
            <span className={styles.errorWrap}>
              {t("clientPolls.modal.unknownType")}
            </span>
          </div>
        );
    }
  };

  /**
   * Aynı groupID'yi taşıyan ardışık soruları tek tablo olarak çizer. Her satır
   * ayrı bir tekli seçim sorusudur; cevap akışı (answers/zorunluluk) değişmez.
   */
  const renderMatrixGroup = (
    group: ClientSurveyQuestionResDto[],
    startIndex: number,
  ) => {
    const first = group[0];
    const columnOptions = [...(first.options ?? [])].sort(
      (a, b) => (a.order ?? 0) - (b.order ?? 0),
    );
    const sortedOptions = (q: ClientSurveyQuestionResDto) =>
      [...(q.options ?? [])].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    return (
      <div key={`group-${first.groupID}-${first.id}`} className={styles.questionBlock}>
        <MatrixQuestionTable
          title={first.groupTitle ?? ""}
          columns={columnOptions.map(getOptionLabel)}
          readOnly={readOnly}
          rows={group.map((q, i) => {
            const selectedId =
              (answers[q.id] as string) ?? q.selectedOptionIds?.[0] ?? "";
            const idx = sortedOptions(q).findIndex((o) => o.id === selectedId);
            return {
              key: q.id,
              number: startIndex + i + 1,
              label: q.questionText,
              required: q.isRequired ?? q.isRequried ?? false,
              selectedIndex: idx >= 0 ? idx : null,
            };
          })}
          onSelect={(rowKey, columnIndex) => {
            const q = group.find((g) => g.id === rowKey);
            const opt = q ? sortedOptions(q)[columnIndex] : undefined;
            if (opt) setAnswer(rowKey, opt.id);
          }}
        />
      </div>
    );
  };

  const renderQuestions = (questions: ClientSurveyQuestionResDto[]) => {
    const sorted = [...questions].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const nodes: React.ReactNode[] = [];
    let i = 0;
    while (i < sorted.length) {
      const q = sorted[i];
      if (q.groupID) {
        let j = i;
        while (j < sorted.length && sorted[j].groupID === q.groupID) j++;
        nodes.push(renderMatrixGroup(sorted.slice(i, j), i));
        i = j;
        continue;
      }
      nodes.push(renderQuestion(q, i));
      i++;
    }
    return nodes;
  };

  const customHeader = (
    <div className={styles.header}>
      <h3 className={styles.headerTitle}>{pollTitle}</h3>
      <button
        type="button"
        className={styles.headerCloseBtn}
        onClick={onClose}
        aria-label={t("common.close")}
      >
        <Close width={24} height={24} color="#505766" />
      </button>
    </div>
  );

  const handleFollowUpNavigate = () => {
    const snapshot = pendingFollowUp;
    setPendingFollowUp(null);
    onClose();
    const resolved = resolveFollowUpAction(snapshot?.actionUrl);
    if (!resolved) return;
    const mergedState =
      resolved.path === "/dashboard/client-polls"
        ? { ...(resolved.state ?? {}), pollTitle: snapshot?.title }
        : resolved.state;
    navigate(resolved.path, mergedState ? { state: mergedState } : undefined);
  };

  const customFooter = readOnly ? (
    <div className={styles.footer}>
      <button
        type="button"
        className={styles.btnCancel}
        onClick={onClose}
        style={{ marginLeft: "auto" }}
      >
        <Close width={18} height={18} color="#505766" />
        {t("common.close")}
      </button>
    </div>
  ) : pendingFollowUp ? (
    <div className={styles.footer}>
      <button
        type="button"
        className={styles.btnCancel}
        onClick={onClose}
      >
        {t("common.later")}
      </button>
      <button
        type="button"
        className={styles.btnFinish}
        onClick={handleFollowUpNavigate}
      >
        <Check width={18} height={18} color="#fff" />
        {pendingFollowUp.buttonLabel ?? t("common.continue")}
      </button>
    </div>
  ) : isCompleted ? (
    <div className={styles.footer}>
      <button
        type="button"
        className={styles.btnCancel}
        onClick={onClose}
        style={{ marginLeft: "auto" }}
      >
        {t("common.close")}
      </button>
      <button
        type="button"
        className={styles.btnFinish}
        onClick={() => {
          onClose();
          navigate("/dashboard/client-certificates");
        }}
      >
        <Check width={18} height={18} color="#fff" />
        {t("clientPolls.modal.goToCertificate")}
      </button>
    </div>
  ) : (
    <div className={styles.footer}>
      <button type="button" className={styles.btnCancel} onClick={onClose}>
        <Close width={18} height={18} color="#505766" />
        {t("clientPolls.modal.cancel")}
      </button>
      <button
        type="button"
        className={styles.btnFinish}
        onClick={handleFinish}
        disabled={submitLoading}
      >
        <Check width={18} height={18} color="#fff" />
        {submitLoading ? t("buttons.loading") : t("clientPolls.modal.finish")}
      </button>
    </div>
  );

  const bodyContent = pendingFollowUp ? (
    <div className={styles.successWrap}>
      <div className={styles.successIcon}>
        <Check width={32} height={32} color="#15ac77" />
      </div>
      <h3 className={styles.successTitle}>{pendingFollowUp.title}</h3>
      <p className={styles.successMessage}>{pendingFollowUp.message}</p>
    </div>
  ) : isCompleted ? (
    <div className={styles.successWrap}>
      <div className={styles.successIcon}>
        <Check width={32} height={32} color="#15ac77" />
      </div>
      <h3 className={styles.successTitle}>
        {t("clientPolls.modal.completedTitle")}
      </h3>
      <p className={styles.successMessage}>
        {t("clientPolls.modal.completedMessage")}
      </p>
    </div>
  ) : loading ? (
    <div className={styles.loadingWrap}>
      {t("clientPolls.loading")}
    </div>
  ) : error ? (
    <div className={styles.errorWrap}>{error}</div>
  ) : data?.questions?.length ? (
    <fieldset
      disabled={readOnly}
      className={styles.body}
      style={{
        border: "none",
        padding: 0,
        margin: 0,
        minWidth: 0,
        pointerEvents: readOnly ? "none" : undefined,
      }}
    >
      {data.introText?.trim() && (
        <p className={styles.surveyText}>{data.introText}</p>
      )}
      {renderQuestions(data.questions)}
      {data.closingText?.trim() && (
        <p className={`${styles.surveyText} ${styles.surveyTextClosing}`}>
          {data.closingText}
        </p>
      )}
    </fieldset>
  ) : data ? (
    <div className={styles.loadingWrap}>
      {t("clientPolls.modal.noQuestions")}
    </div>
  ) : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      customHeader={customHeader}
      customFooter={customFooter}
      size="lg"
    >
      {bodyContent}
    </Modal>
  );
};

export default PollAnswerModal;
